import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useReferencingForm } from "../context/ReferencingFormContext";
import OrderReference from "../../Order/OrderReference";
import ProductReferenceListWrapper from "../../Product/ProductReferenceListWrapper";
import { OrderReference as OrderReferenceType } from "$types/OrderReference";
import ReferencingType from "$types/ReferencingType";
import { ProductReferenceList } from "$types/ProductReferenceList";
import ReferenceType from "$types/ReferenceType";
import useReferencingCategory from "$hooks/useReferencingCategory";
import { useDefaultValueReferencing } from "../../useDefaultValueReferencing";

export const ReferencesSection: React.FC = () => {
  const {
    focusedReceptionReferenceId,
    isReadOnly,
    warehouse,
    reception,
    productRefListFields,
    orderRefFields,
    editedBlocks,
    addToEditedBlocks,
    removeFromEditedBlocks,
    refreshRefItem,
    handleOrderReference,
    fromOrderToProductReferencing,
    fromProductToOrderReferencing,
    prependProductRefList,
    removeOrderRef,
    prependOrderRef,
    removeProductRefList,
    referencingCategory,
    scrollToRef,
    confirmedDeletions,
    openDeleteModal,
    deletePreviousOrderRef,
    deletePreviousProductListRef,
    getPreviousProductListFromOrderRef,
    setOpenDeleteModal,
    setToKeepReferences,
    setToKeepIdenticalReferences,
    setToDeleteOrderRef,
    isDeletable,
    getPreviousOrderRefFromProductList,
    setToDeleteReferences,
    setToDeleteProductRefList,
    setIndexToDelete,
  } = useReferencingForm();

  const [hasFocused, setHasFocused] = useState(false);

  useEffect(() => {
    if (focusedReceptionReferenceId !== undefined) {
      setHasFocused(false);
    }
  }, [focusedReceptionReferenceId]);

  const { hasReferencingCategory } = useReferencingCategory(reception.company);
  const form = useFormContext();

  const { defaultValues } = useDefaultValueReferencing({
    companyId: reception.company,
    reception,
  });

  const isAnOrderRef = (x: any) => !!x?.referenceType; // orderReference has a referenceType, productRefList doesn't

  const allRefsSorted = useMemo(() => {
    // Prefer API suborderReferences; fall back to hydrated suborders.
    // Never call localeCompare on undefined (empty/partial refs crash the drawer).
    const getRefSortKey = (item: OrderReferenceType | ProductReferenceList) => {
      if (isAnOrderRef(item)) {
        const orderRef = item as OrderReferenceType;
        return (
          orderRef.suborderReferences?.[0] ??
          orderRef.suborders?.[0]?.reference ??
          ""
        );
      }
      return (item as ProductReferenceList).suborders?.[0]?.reference ?? "";
    };

    return [...productRefListFields, ...orderRefFields].sort((x, y) =>
      getRefSortKey(x).localeCompare(getRefSortKey(y))
    );
  }, [orderRefFields, productRefListFields]);

  const onCancelEdit = (id: string, index: number, type: ReferenceType) => {
    removeFromEditedBlocks({ id, type });

    if (type === ReferenceType.ORDER) {
      const defaultOrderValues = defaultValues?.orderReferences?.[index];
      if (defaultOrderValues) {
        form.setValue(`orderReferences.${index}`, defaultOrderValues);
      } else {
        form.resetField(`orderReferences.${index}`);
      }
    }

    if (type === ReferenceType.PRODUCT) {
      // Instead of relying on defaultValues, get the current data from productRefListFields
      const currentProductRefList = productRefListFields[index];

      if (currentProductRefList) {
        // Reset to the current field array data (which should be the original data)
        form.setValue(`productReferenceLists.${index}`, currentProductRefList);
      } else {
        // Fallback to defaultValues
        const defaultProductValues =
          defaultValues?.productReferenceLists?.[index];
        if (defaultProductValues) {
          form.setValue(`productReferenceLists.${index}`, defaultProductValues);
        } else {
          // Don't use resetField as it can cause undefined values
          // Instead, find the original data and restore it
          const originalData = allRefsSorted.find(
            (item) =>
              !isAnOrderRef(item) &&
              String((item as ProductReferenceList).id) === id
          );

          if (originalData) {
            form.setValue(`productReferenceLists.${index}`, originalData);
          }
        }
      }
    }
  };

  const onDeleteOrderRef = async (index: number) => {
    const orderRef = orderRefFields[index];
    const toDelete: string[] = [];
    const toKeep: string[] = [];
    const { productReference } = orderRef;
    const res = await isDeletable(
      productReference,
      reception.company,
      reception.id
    );
    if (res) {
      toDelete.push(productReference);
    } else {
      toKeep.push(productReference);
    }

    if (orderRef.id) {
      // This orderRef was previously created: everything will be handled later
      // in the delete modal and onDeleteProducts function
      setToDeleteReferences(toDelete);
      setToKeepReferences(toKeep);
      setToDeleteOrderRef(orderRef);
    } else {
      // This is a new orderRef: it can be safely deleted
      removeOrderRef(index);
      deletePreviousOrderRef(orderRef);
      deletePreviousProductListRef(
        getPreviousProductListFromOrderRef(orderRef)
      );
    }
  };

  const onDeleteProductRefList = async (index: number) => {
    const pRefList = productRefListFields[index];
    const toDelete: string[] = [];

    const hasExistingReferences = pRefList.productReferences.some(
      (ref) => ref.id
    );

    if (hasExistingReferences) {
      // Always add ALL product references to toDelete for reception reference deletion
      // Even if the underlying products can't be deleted from the database
      pRefList?.productReferences?.forEach((receptionRef) => {
        const { productReference } = receptionRef;
        toDelete.push(productReference);
      });

      setToDeleteReferences(toDelete);
      setToKeepReferences([]);
      setToKeepIdenticalReferences([]);
      setToDeleteProductRefList(pRefList);

      // Always open delete modal when there are existing references
      if (!openDeleteModal && toDelete.length > 0) {
        setOpenDeleteModal(true);
      }
    } else {
      // This is a new productRefList: it can be safely deleted
      removeProductRefList(index);
      deletePreviousProductListRef(pRefList);
      deletePreviousOrderRef(getPreviousOrderRefFromProductList(pRefList));
    }
  };

  return (
    <div>
      {allRefsSorted?.map((refItem, mapIndex) => {
        // à la réception
        if (isAnOrderRef(refItem)) {
          const orderRefItem = refItem as OrderReferenceType;
          const shouldFocus =
            !hasFocused && focusedReceptionReferenceId === orderRefItem.id;
          if (shouldFocus) setHasFocused(true);
          // Prefer object identity (same refs as useFieldArray). Matching only on
          // suborderReferences breaks when several order refs share the same set
          // (all inputs bind to the first match → wrong designation on save).
          let index = orderRefFields.indexOf(orderRefItem);
          if (index < 0) {
            index = orderRefFields.findIndex(
              (x) =>
                (orderRefItem.id != null &&
                  x?.id != null &&
                  String(x.id) === String(orderRefItem.id)) ||
                (x?.productReference === orderRefItem.productReference &&
                  Number(x?.quantity) === Number(orderRefItem.quantity) &&
                  JSON.stringify(x?.suborderReferences || []) ===
                    JSON.stringify(orderRefItem.suborderReferences || []))
            );
          }

          // à la réception
          return (
            <OrderReference
              addToEditedBlocks={addToEditedBlocks}
              companyId={reception.company}
              warehouse={warehouse}
              editedBlocks={editedBlocks}
              handleOnCancelEdit={onCancelEdit}
              hasReferencingCategory={hasReferencingCategory}
              index={index}
              setIndexToDelete={setIndexToDelete}
              isFocused={shouldFocus}
              key={`order-ref-${orderRefItem.id}-${mapIndex}`}
              onDelete={onDeleteOrderRef}
              onReferenceTypeChange={() => {
                removeFromEditedBlocks({
                  id: String(orderRefItem.id || orderRefItem.id),
                  type: ReferenceType.ORDER,
                });
                // Only clear the specific order reference being converted, not ALL of them
                // form.resetField(`orderReferences.${index}`);

                addToEditedBlocks({
                  id: String(orderRefItem.suborders[0]?.id || orderRefItem.id),
                  type: ReferenceType.PRODUCT,
                });

                return fromOrderToProductReferencing(
                  index,
                  orderRefItem,
                  prependProductRefList,
                  orderRefItem,
                  removeOrderRef
                );
              }}
              orderRefItem={orderRefItem}
              reception={reception}
              scrollToRef={scrollToRef}
              downloadOrderReference={() =>
                handleOrderReference(
                  {
                    referenceType: ReferencingType.ALARECEPTION,
                    productRefList: refreshRefItem(orderRefItem),
                  },
                  true
                )
              }
              createOrderReference={() =>
                handleOrderReference(
                  {
                    referenceType: ReferencingType.ALARECEPTION,
                    productRefList: refreshRefItem(orderRefItem),
                  },
                  false
                )
              }
            />
          );
        }

        // à la l'article
        // Product ref list handling
        const productRefList = refItem as ProductReferenceList;
        const shouldFocus =
          !hasFocused &&
          productRefList?.productReferences?.some(
            (pr) => pr.id === focusedReceptionReferenceId
          );

        if (shouldFocus) setHasFocused(true);

        let index = productRefListFields.indexOf(productRefList);
        if (index < 0) {
          index = productRefListFields.findIndex(
            (x) =>
              x.productReferences?.length ===
                productRefList?.productReferences?.length &&
              x.productReferences?.every((ref) =>
                productRefList?.productReferences?.some((p) => p.id === ref.id)
              )
          );
        }

        return (
          <ProductReferenceListWrapper
            key={`product-ref-${productRefList?.id}-${mapIndex}`}
            addToEditedBlocks={addToEditedBlocks}
            companyId={reception.company}
            warehouse={warehouse}
            editedBlocks={editedBlocks}
            handleOnCancelEdit={onCancelEdit}
            hasReferencingCategory={hasReferencingCategory}
            index={index}
            isFocused={shouldFocus}
            isReadOnly={isReadOnly}
            onDelete={onDeleteProductRefList}
            setIndexToDelete={setIndexToDelete}
            onReferenceTypeChange={() => {
              // Only clear the specific product reference list being converted, not ALL of them
              // form.resetField(`productReferenceLists.${index}`);

              removeFromEditedBlocks({
                id: String(
                  productRefList.suborders[0]?.id || productRefList.id
                ),
                type: ReferenceType.PRODUCT,
              });
              addToEditedBlocks({
                id: String(
                  productRefList.suborders[0]?.id || productRefList.id
                ),
                type: ReferenceType.ORDER,
              });

              const result = fromProductToOrderReferencing(
                referencingCategory,
                prependOrderRef,
                index,
                productRefListFields[index],
                removeProductRefList
              );
              return result;
            }}
            scrollToRef={scrollToRef}
            confirmedDeletions={confirmedDeletions}
            openDeleteModal={openDeleteModal}
            productRefList={productRefList}
            reception={reception}
            downloadOrderReference={() =>
              handleOrderReference(
                {
                  referenceType: ReferencingType.ALARTICLE,
                  productRefList: {
                    productReferences: productRefList.productReferences,
                    suborders: productRefList.suborders,
                  },
                },
                true
              )
            }
            createOrderReference={() =>
              handleOrderReference(
                {
                  referenceType: ReferencingType.ALARTICLE,
                  productRefList: {
                    productReferences: productRefList.productReferences,
                    suborders: productRefList.suborders,
                  },
                },
                false
              )
            }
          />
        );
      })}
    </div>
  );
};

export default ReferencesSection;
